import { useEffect, useState } from 'react'
import { ChartWrapperOptions } from 'react-google-charts'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useLocation from 'wouter/use-location'
import ConfirmModal from '../../components/ConfirmModal'
import HeaderBanner from '../../components/DesignSystem/components/molecules/HeaderBanner/HeaderBanner.component'
import { DropdownButton } from '../../components/DropdownButton'
import { NoData } from '../../components/GoogleChart/ChartComponent'
import WarningBlock from '../../components/WarningBlock'
import { capitalize } from '../../helpers/formatter'
import { toUtcViewDate } from '../../helpers/formatter/dateFormatters'
import FullLayout from '../../layouts/FullLayout'
import { useNetwork } from '../Network/Context/NetworkContext'
import { NetworkCohortsSelect } from '../Network/UnifiedReports/NetworkCohortsSelect'
import LoadingPage from '../Other/LoadingPage'
import { useReports } from './Context/ReportsContext'
import { useTeam } from './Context/TeamContext'
import { useCohorts } from './Hooks/useCohorts'
import { useCsvReport } from './Hooks/useCsvReport'
import { DateFilterProps, getDailyMonitoring } from './Reports/apis/api-reports'
import { ButtonExport } from './Reports/Components/ButtonExport'
import { CourseCohortsSelect } from './Reports/Components/CourseCohortsSelect'
import { FilterDates } from './Reports/Components/FilterDates'
import { Menu } from './Reports/Components/Menu'

export const Reports = ({ skipLayout = false }: ReportsProps = {}) => {
    const { t } = useTranslation('teamReports')
    const [, setLocation] = useLocation()
    const { team, courseID, mainLinks, networkId } = useTeam()
    const { networkName } = useNetwork()
    const { cohorts, lastMigrationDate, isLoading, isError } = useCohorts()
    const { reportQuery } = useReports()
    const { getFilteredReport } = useCsvReport()
    const [csvQuery, setCsvQuery] = useState<DateFilterProps>({ ...reportQuery })
    const [dateSelectorModal, setDateSelectorModal] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const formattedDate = toUtcViewDate(lastMigrationDate, 'HH:hh')

    // Detect context to build correct PDF URL
    const isHierarchicalRoute = !!networkId && window.location.pathname.includes('/network/')
    const isUnifiedReports = window.location.pathname === `/network/${networkId}/reports`

    // Build PDF URL with filter query params to preserve state across navigation
    const buildPdfUrl = () => {
        const basePath = isUnifiedReports
            ? `/network/${networkId}/reports/pdf`
            : isHierarchicalRoute
            ? `/network/${networkId}/cohorts/${courseID}/reports/pdf`
            : `/admin/reports/${courseID}/pdf`

        const params = new URLSearchParams()
        if (reportQuery.cohorts?.length) params.set('cohorts', reportQuery.cohorts.join(','))
        if (reportQuery.from) params.set('from', reportQuery.from)
        if (reportQuery.to) params.set('to', reportQuery.to)

        const queryString = params.toString()
        return queryString ? `${basePath}?${queryString}` : basePath
    }

    const pdfUrl = buildPdfUrl()

    const onConfirm = async () => {
        const response = await getFilteredReport(getDailyMonitoring, 'dailyMonitoring', csvQuery)
        if (response) {
            setDateSelectorModal(false)
            setShowDropdown(false)
        }
    }

    const onCancel = () => {
        setDateSelectorModal(false)
        setShowDropdown(false)
    }

    useEffect(() => {
        sessionStorage.setItem('last-courseId', courseID || '')
    }, [courseID])

    if (!team) return null

    const {
        hq: { name: hqName },
        teamName,
    } = team

    if (isLoading) return <LoadingPage />

    if (isError) return <NoData text={t('An error occurred while fetching data')} />

    const reportsContent = (
        <>
            <WarningBlock />
            <ReportStyled className="container">
                <HeaderBanner
                    title={isUnifiedReports ? networkName : teamName}
                    subtitle={t('reports')}
                    subActions={
                        <DropdownButton
                            label={t('Download')}
                            dropdownIcon={true}
                            icon={['fas', 'download']}
                            showDropdown={showDropdown}
                            setShowDropdown={setShowDropdown}
                            options={[
                                <>
                                    <ButtonExport
                                        onClick={() => setLocation(pdfUrl, { replace: false })}
                                        label="PDF"
                                        disabled={!cohorts || cohorts.length === 0}
                                        type="outlined small"
                                        icon={['fas', 'print']}
                                    />
                                    <ButtonExport
                                        onClick={() => setDateSelectorModal(true)}
                                        label="CSV"
                                        disabled={!cohorts || cohorts.length === 0}
                                        type="outlined small"
                                        icon={['fas', 'file-csv']}
                                    />
                                </>,
                            ]}
                        ></DropdownButton>
                    }
                />
                <div className="span-stats">
                    {t('Last updated')} {formattedDate} hs
                </div>
                {!cohorts || cohorts.length === 0 ? (
                    <NoData text={`You don't have cohorts yet`} />
                ) : (
                    <>
                        {isUnifiedReports ? <NetworkCohortsSelect /> : <CourseCohortsSelect />}
                        <Menu />
                    </>
                )}
            </ReportStyled>
            {dateSelectorModal && (
                <ConfirmModal
                    title={t('Select cohorts and date range')}
                    open={dateSelectorModal}
                    confirmButtonText="Confirm"
                    cancelButtonText="Cancel"
                    onConfirm={onConfirm}
                    onClose={onCancel}
                    contentText={<FilterDates setCsvQuery={setCsvQuery} />}
                />
            )}
        </>
    )

    if (skipLayout) {
        return reportsContent
    }

    return (
        <FullLayout links={mainLinks} ruta={`${capitalize(hqName)} > ${capitalize(teamName)}`}>
            {reportsContent}
        </FullLayout>
    )
}

export default Reports

interface ReportsProps {
    skipLayout?: boolean
}

export const ReportStyled = styled.section`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 1rem 0;
    h2 {
        margin: 0;
    }
    & .loading-page-container {
        height: 6em;
    }
    & .cohort-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`

export type PropsReports = {
    barChartOptions?: {
        width?: string
        height?: string
        options?: ChartWrapperOptions['options']
    }
    pieChartOptions?: {
        width?: string
        height?: string
        options?: ChartWrapperOptions['options']
    }
}
